var _a;
import { buildClickUIElementEvent } from './engagement/ui-element/event-builder.js';
import { mergeExperiments } from './utils/merge-experiments';
import { pageFromPageView } from './utils/page-from-page-view.js';
import { buildViewArticleEvent } from './view/article/event-builder.js';
import { buildViewFrontpageEvent } from './view/frontpage/event-builder.js';
import { buildViewListingEvent } from './view/listing/event-builder.js';
import { buildViewLoginPosterEvent } from './view/loginposter/event-builder.js';
import { buildViewPageEvent } from './view/page/event-builder.js';
import { buildViewSalesPosterEvent } from './view/salesposter/event-builder.js';
import { buildViewUIElementEvent } from './view/ui-element/event-builder.js';
import { VisibilityObserver } from './view/ui-element/visibility-observer.js';
function isWindowDefined() {
    return typeof window !== 'undefined';
}
class NewsMediaPulseTracker {
    constructor(pulseTracker) {
        this.visibilityObservers = new Map();
        this.currentPage = {
            id: 'undefined-id',
            type: 'Page',
            url: isWindowDefined() ? window.location.href : '',
        };
        this.updateBaseEvent = (eventProperties, mergeWithPreviousState = true) => {
            const { experiments, ...rest } = eventProperties;
            if (Array.isArray(experiments) || experiments instanceof Promise) {
                if (mergeWithPreviousState) {
                    this.baseExperiments = mergeExperiments(this.baseExperiments, experiments);
                }
                else {
                    this.baseExperiments = mergeExperiments(undefined, experiments);
                }
                this.pulseTracker.update({ experiments: this.baseExperiments }, false);
            }
            return this.pulseTracker.update(rest, mergeWithPreviousState);
        };
        this.trackViewArticle = (inputProperties, trackOptions) => {
            const viewArticleEventData = this.enrichWithExperiments(buildViewArticleEvent(inputProperties));
            this.currentPage = pageFromPageView(viewArticleEventData);
            return this.pulseTracker.trackPageView(viewArticleEventData, trackOptions);
        };
        this.trackViewLoginPoster = (inputProperties, trackOptions) => {
            const viewLoginPosterEventData = this.enrichWithExperiments(buildViewLoginPosterEvent(inputProperties));
            this.currentPage = pageFromPageView(viewLoginPosterEventData);
            return this.pulseTracker.trackPageView(viewLoginPosterEventData, trackOptions);
        };
        this.trackViewSalesPoster = (inputProperties, trackOptions) => {
            const viewSalesposterEventData = this.enrichWithExperiments(buildViewSalesPosterEvent(inputProperties));
            this.currentPage = pageFromPageView(viewSalesposterEventData);
            return this.pulseTracker.trackPageView(viewSalesposterEventData, trackOptions);
        };
        this.trackViewListing = (inputProperties, trackOptions) => {
            const viewListingEventData = this.enrichWithExperiments(buildViewListingEvent(inputProperties));
            this.currentPage = pageFromPageView(viewListingEventData);
            return this.pulseTracker.trackPageView(viewListingEventData, trackOptions);
        };
        this.trackViewFrontpage = (inputProperties, trackOptions) => {
            const viewFrontpageEventData = this.enrichWithExperiments(buildViewFrontpageEvent(inputProperties));
            this.currentPage = pageFromPageView(viewFrontpageEventData);
            return this.pulseTracker.trackPageView(viewFrontpageEventData, trackOptions);
        };
        this.trackViewPage = (inputProperties, trackOptions) => {
            const pageViewEventData = this.enrichWithExperiments(buildViewPageEvent(inputProperties));
            this.currentPage = pageFromPageView(pageViewEventData);
            return this.pulseTracker.trackPageView(pageViewEventData, trackOptions);
        };
        this.addPageLeaveTracking = (objectElement, pageElement, eventBuilder, options) => this.pulseTracker.addPageLeaveTracking(objectElement, pageElement, eventBuilder, options);
        this.removePageLeaveTracking = () => this.pulseTracker.removePageLeaveTracking();
        this.updatePageLeaveTracking = (objectElement, pageElement, resetProperties) => this.pulseTracker.updatePageLeaveTracking(objectElement, pageElement, resetProperties);
        this.trackPageLeave = (eventInput, eventName) => this.pulseTracker.trackPageLeave(eventInput, eventName);
        this.trackActivePageLeave = (eventInput, eventName) => this.pulseTracker.trackActivePageLeave(eventInput, eventName);
        this.trackViewUIElement = (inputProperties, trackOptions) => {
            const viewUIElementEventData = this.enrichWithExperiments(buildViewUIElementEvent(inputProperties, this.currentPage));
            return this.pulseTracker.track('trackerEvent', viewUIElementEventData, trackOptions);
        };
        /**
         * Tracks visibility of elements matching the selector and sends a View UIElement event.
         * @param {string} selector - CSS selector for elements to track.
         * @param {Function} buildEventFromElement - Function to build event object when element is visible.
         */
        this.addUIElementVisibilityTracking = (selector, buildEventFromElement) => {
            if (this.visibilityObservers.has(selector)) {
                console.warn(`Cannot add UI Element visibility tracking for selector "${selector}" as it is already being tracked.`);
                return;
            }
            this.visibilityObservers.set(selector, new VisibilityObserver((element) => {
                const viewUIElementInputProperties = buildEventFromElement(element);
                this.trackViewUIElement(viewUIElementInputProperties);
            }));
            this.visibilityObservers.get(selector)?.observe(selector);
        };
        this.trackClickUIElement = (inputProperties, trackOptions) => {
            const clickUIElementEventData = this.enrichWithExperiments(buildClickUIElementEvent(inputProperties, this.currentPage));
            return this.pulseTracker.track('trackerEvent', clickUIElementEventData, trackOptions);
        };
        this.addExperiments = (experiments) => {
            this.updateBaseEvent({ experiments });
        };
        this.enrichWithExperiments = (eventProperties) => {
            const experiments = mergeExperiments(this.baseExperiments, eventProperties.experiments);
            if (experiments) {
                return {
                    ...eventProperties,
                    experiments,
                };
            }
            return eventProperties;
        };
        this.pulseTracker = pulseTracker;
    }
}
_a = NewsMediaPulseTracker;
NewsMediaPulseTracker.fromPulseTracker = (PulseTrackerClass, trackerConfig) => {
    if (isWindowDefined() && window.nmTrackerSync) {
        console.warn('NM Pulse tracker already initialized. Returning existing instance.');
        return window.nmTrackerSync;
    }
    if (isWindowDefined() && window.nmTracker) {
        throw new Error('Attempting to call fromAutotracker after fromPulseTracker has been called. This is not supported.');
    }
    const { providerId, productName, deployStage, builders, sdkConfigInput = {}, } = trackerConfig;
    builders.provider = Object.assign(builders.provider || {}, {
        ...builders.provider,
        id: providerId,
        product: productName,
        productType: 'ResponsiveWeb',
    });
    const sdkConfig = {
        deployStage,
        ...sdkConfigInput,
    };
    const pulseTracker = new PulseTrackerClass(providerId, sdkConfig, builders);
    const nmTracker = new _a(pulseTracker);
    if (isWindowDefined() && window !== null) {
        window.nmTrackerSync = nmTracker;
    }
    return nmTracker;
};
NewsMediaPulseTracker.fromAutotracker = async (pulseAutotracker, trackerConfig, trackerId) => {
    if (isWindowDefined() && window.nmTracker) {
        console.warn('NM Pulse tracker already initialized. Returning existing instance.');
        return window.nmTracker;
    }
    if (isWindowDefined() && window.nmTrackerSync) {
        throw new Error('Attempting to call fromAutotracker after fromPulseTracker has been called. This is not supported.');
    }
    const nmTracker = new Promise((resolve) => {
        const { providerId, productName, deployStage, builders, sdkConfigInput = {}, } = trackerConfig;
        builders.provider = Object.assign(builders.provider || {}, {
            ...builders.provider,
            id: providerId,
            product: productName,
            productType: 'ResponsiveWeb',
        });
        const sdkConfig = {
            deployStage,
            ...sdkConfigInput,
        };
        pulseAutotracker('init', providerId, sdkConfig, builders, trackerId);
        if (trackerId) {
            pulseAutotracker(trackerId, (pulseTracker) => {
                resolve(new _a(pulseTracker));
            });
        }
        else {
            pulseAutotracker((pulseTracker) => {
                resolve(new _a(pulseTracker));
            });
        }
    });
    if (isWindowDefined()) {
        window.nmTracker = nmTracker;
    }
    return nmTracker;
};
export { NewsMediaPulseTracker };
//# sourceMappingURL=NewsMediaPulseTracker.js.map